// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartForm.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartForm model
 */
const ASC = 'ASC';
const DESC = 'DESC';
const UNSORTED = 'Unsorted';
import {
  filter,
  isEmpty,
  every,
  map,
  isNil,
  flow,
  getOr,
  omit,
  get,
  forEach,
  trim,
  clone,
  compact,
  assign,
  find,
  isUndefined,
  omitBy,
  debounce,
  invokeArgs,
  concat,
  isNull,
  replace,
  cloneDeep,
  last,
  isArray,
  findIndex,
  head,
  escape
} from 'lodash/fp';

import { observable, computed, action, toJS, extendObservable, transaction } from 'mobx';
import ReportPartElement from './Izenda.ReportPartElement';
import constants from 'logic/data/Izenda.ReportPartFormConstants';
import reportBodyConstants from 'logic/data/Izenda.ReportBodyConstants';
import ReportUtils from './Izenda.ReportUtils';
import TinyMCEConstants from 'logic/data/Izenda.TinyMCEConstants';
import reportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import ReportPartFormUtils from 'logic/models/Izenda.ReportPartFormUtils';
import ReportPartFormEmbeddedReport from './Izenda.ReportPartFormEmbeddedReport';
import ReportPartFormSmartTag from './Izenda.ReportPartFormSmartTag';
import ReportPartFormField from './Izenda.ReportPartFormField';
import ReportPartFormRepeater from './Izenda.ReportPartFormRepeater';
import uiConstants from 'logic/data/Izenda.UIConstants';
import { FieldPropertiesPaths } from 'logic/data/Izenda.FormFieldPropertiesPaths';
import { FUNCTION } from 'logic/data/Izenda.ReportPartFieldConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import tinymce from 'tinymce/tinymce';
import RepeaterNode from './Izenda.RepeaterNode';
/**
 * create a container from data from api
 * @param  {Object} data [data from api]
 * @return {container}   [container with elements typeof ReportPartElement]
 */
const populateReportPartContainer = (name: string, data: any) => {
  if (!data) {
    return {
      elements: [],
      name,
      visibleElement: ReportUtils.getVisibleField
    };
  }

  const pickData = omit('elements', data);
  const elements = data.elements;
  /* convert elements json object to become list of ReportPartElement*/
  const convertElements = map(item => new ReportPartElement().readData(item), elements);

  extendObservable(pickData, {
    elements: convertElements || [],
    name,
    visibleElement: ReportUtils.getVisibleField
  });

  return pickData;
};

/**
 * Rebuild the HTML markup from string content
 */
const populateHtmlContent = (content: any, elements: Array<any>) => {
  let domContent = $izenda('<div>' + content + '</div>');
  domContent.find('field').each((index, item) => {
    let fieldNameAlias = trim($izenda(item).text());

    if (fieldNameAlias === reportBodyConstants.FIELD_HIDDEN_LABEL) {
      const fieldName = $izenda(item).attr('field-name');
      const matches = fieldName.match(/\[(.*?)\]/g);
      fieldNameAlias = matches[matches.length - 1].replace('[', '').replace(']', '');
    }

    let element = find({ name: fieldNameAlias }, elements);
    if (element && element.field.isCalculated) {
      $izenda(item)
        .find('[key="fieldId"]')
        .attr('value', element.fieldId);
    }

    if (element && isNil(get('properties.drillDown.subReport.selectedReport', element))) {
      removeInvalidSubReport(item);
    }

    if (element && !element.field.visible) {
      $izenda(item).html(
        $izenda(item)
          .children()
          .filter(function() {
            let isFieldProp = $izenda('field-prop', this);
            let hasCancelButton = $izenda('.icon-cancel', this);
            return isFieldProp || hasCancelButton;
          })
      );
      $izenda(item)
        .find('[class="icon-cancel"]')
        .prev()
        .after(reportBodyConstants.FIELD_HIDDEN_LABEL);
    }

    let fieldId = $izenda(item)
      .find('[key="fieldId"]')
      .attr('value');
    if (find({ fieldId }, elements)) return;
    $izenda(item).remove();
  });

  return domContent.html();
};

/**
 * Remove invalid subReport from html.
 * This is intent to clean html if there is no setting for sub-report on field.
 * This case occurs in copy report.
 * @param  {Object} $field
 */
const removeInvalidSubReport = $field => {
  const fieldKeys = [
    FieldPropertiesPaths.subReportSelReport.key,
    FieldPropertiesPaths.subReportStyle.key,
    FieldPropertiesPaths.subReportReportPartUsed.key,
    FieldPropertiesPaths.subReportFilter.key,
    FieldPropertiesPaths.subReportMappingFields.key,
    FieldPropertiesPaths.subReportSelectedIconValue.key,
    FieldPropertiesPaths.subReportSelectedIcon.key,
    FieldPropertiesPaths.subReportViewSettingByLink.key
  ];

  forEach(key => {
    $izenda('[key="' + key + '"]', $field).remove();
  }, fieldKeys);

  $izenda('.icon-menu', $field).remove();
};

@tracker
export default class ReportPartForm {
  @observable
  isCrossFiltering = false;
  @observable
  type = reportDesignerConstants.REPORT_PART_TYPE.Form;
  @observable
  editorControl = {};

  selectedFieldElement = {};
  @observable
  activeTabKey = '';
  @observable
  isSelectedCell = false;
  @observable
  isActiveForEdit = false;
  @observable
  properties = {
    format: {
      heading: { h1: false, h2: false, h3: false, h4: false, h5: false, h6: false },
      block: { p: true, blockquote: false, div: false, pre: false },
      font: {
        bold: false,
        italic: false,
        underline: false,
        strikethrough: false,
        superscript: false,
        subscript: false,
        code: false,
        fontFamily: uiConstants.defaultFontFamily,
        fontSize: uiConstants.defaultFontSize,
        textColor: uiConstants.textColor,
        backgroundColor: uiConstants.backgroundColor
      },
      alignment: { alignleft: false, aligncenter: false, alignright: false, alignjustify: false },
      list: { bullet: '', numbered: '' }
    },
    pasteAsText: false,
    highlightCode: true,
    wrapText: true,
    mceDirectionRTL: false,
    mceDirectionLTR: false,
    mceVisualChars: false,
    mceToggleVisualAid: true,
    usePagination: true,
    pageSize: reportDesignerConstants.DEFAULT_PAGE_SIZE,
    pageBreakAfterEachEntry: false,
    dataRefreshInterval: { enable: false, updateInterval: 0, isAll: true, latestRecord: 0 }
  };
  @observable
  columns = {};
  @observable
  separators = {};

  isFormatted: boolean = false;
  reportPartFormRepeater: ReportPartFormRepeater = null;
  reportPartFormEmbeddedReport: ReportPartFormEmbeddedReport = null;
  reportPartFormSmartTag: ReportPartFormSmartTag = null;
  reportPartFormField: ReportPartFormField = null;
  formState: any = null;
  htmlContent: any = null;
  dataTree: any = null;
  autoSize: boolean = false;

  constructor(data) {
    this.reportPartFormRepeater = new ReportPartFormRepeater();
    this.reportPartFormEmbeddedReport = new ReportPartFormEmbeddedReport();
    this.reportPartFormSmartTag = new ReportPartFormSmartTag();
    this.reportPartFormField = new ReportPartFormField();

    this.formState = {};
    this.htmlContent = '';

    this.columns = populateReportPartContainer('columns');
    this.separators = populateReportPartContainer('separators');

    data && this.readData(data);

    this.cleanDirty(); // reset dirty
  }

  @computed
  get optionalParams() {
    if (this.properties.usePagination) {
      return {
        expandedLevel: reportDesignerConstants.EXPANDED_LEVEL.ALL,
        paging: { pageIndex: reportDesignerConstants.DEFAULT_PAGE_INDEX, pageSize: this.properties.pageSize }
      };
    } else {
      return {};
    }
  }

  @computed
  get reportPartStyle() {
    return this.type;
  }

  @computed
  get allVisibleElements() {
    return this.columns.visibleElement.concat(this.separators.visibleElement);
  }

  @computed
  get containerCollection() {
    return compact([this.columns, this.separators]);
  }

  @computed
  get isBeingBuild() {
    return !isEmpty(this.allVisibleElements);
  }

  @computed
  get dataRefreshInterval() {
    return get('dataRefreshInterval', this.properties);
  }

  /**
   * Reuild html
   */
  rebuildHtml(): void {
    // Convert repeater tag to comment tag
    this.convertRepeaterTag(0, constants.TAB_VISUAL.key);

    this.htmlContent = populateHtmlContent(
      this.htmlContent,
      get('columns.elements', this).concat(get('separators.elements', this).slice())
    );

    // Fix for IE. Attribute order not guaranteed by specification.
    this.htmlContent = this.htmlContent.replace(/<field-prop (value=\".+?\") (key=\".+?\")>/g, '<field-prop $2 $1>');
  }

  /**
   * buildDataForSave
   * @return {Object}
   */
  buildDataForSave(): any {
    // Need call before get data when HTML tabs all save or call save
    this.rebuildHTMLContent();
    this.rearrangeFieldInColumnContainer();
    !isEmpty(this.dataTree) && this.syncFromHTMLToElements();
    this.updateSubtotalLevels();

    const formData = omit(['editorControl', 'selectedFieldElement', 'repeaterNodes'], this);
    const properties = toJS(this.properties);

    return assign({ properties }, formData);
  }

  /**
   * Set query source tree.
   * @param  {Object} dataTree
   */
  setDataTree(dataTree: Array<any>): void {
    this.dataTree = dataTree;
  }

  /**
   * Transform field tags to column's elements
   */
  syncFromHTMLToElements(reportPart: any): void {
    //
    const reportPartConfig = reportPart || this;
    !isEmpty(this.activeTabKey) &&
      this.activeTabKey !== constants.TAB_VISUAL.key &&
      !isEmpty(this.dataTree) &&
      ReportPartFormUtils.syncFromHTMLToElements(
        this.columns,
        this.separators,
        this.htmlContent,
        this.dataTree,
        reportPartConfig
      );
  }

  /**
   * getReportPartProperties description
   * @param  {String} propKey
   * @param  {Object|Array|String} defaultValue
   * @return {Object|Array|String}
   */
  getReportPartProperties(propKey: string, defaultValue: any): any {
    const props = this.properties || {};

    if (!propKey) {
      return props;
    }

    return getOr(defaultValue, propKey, props);
  }

  /**
   * setReportPartProperties description
   * @param {Object} newProps [description]
   */
  setReportPartProperties(newProps: any): any {
    const props = toJS(this.properties) || {};

    transaction(() => {
      let source = newProps || {};
      source = omitBy(isUndefined, source);
      extendObservable(props, source);
    });

    this.properties = props;
    this.markDirty();
  }

  /**
   * validate function
   * @method validateFunction
   * @param  {object}         container    container need validateFunction
   * @param  {object}         functionInfo Function need validate
   * @return {json}                      validate result
   */
  validateFunction(container: any, functionInfo: any): any {
    return {
      status: true
    };
  }

  /**
   * Return data object that represents for a report part.
   */
  getData(): any {
    return omit('dataSource', clone(this));
  }

  /**
   * Set HTML Content
   * @param {String} content [description]
   */
  setHTMLContent(content: 'string', reportPart: any): void {
    reportPart && !reportPart.isLoadingData && this.htmlContent !== content && (reportPart.isLoadingData = true);
    this.htmlContent = content;
    this.updateElementPropsForRepeater.apply(this);
  }

  /**
   * Get HTML Code Field
   * @param  {Object} field
   * @return {String}
   */
  getHTMLCodeField(newField: any, dataObject: any): string {
    const fieldName = newField.field.fieldName;
    let text = '';
    const querySourceId = get('field.querySourceId', newField);

    // in case of the field is calculated and it is Report calculated field (querySourceId has value and !== empty)
    // we just need fieldName to build HTML content
    // but if this is Data Model calculated field. We need to build HTML content like other normal field of data model.
    // That must have connection string + schema + table and field name under 
    // format[connectionName].[schema].[DataModel].[FieldName].
    // For ex: [northwind].[dbo].[Orders].[calculated field name]
    if (newField.field.isCalculated && (isNil(querySourceId) || querySourceId === reportDesignerConstants.EMPTY_GUID)) {
      text = `[${fieldName}]`;
    } else {
      const { connectionName, querySourceCategoryName, name } = dataObject;
      text = `[${connectionName}].[${querySourceCategoryName}].[${name}].[${fieldName}]`;
    }
    text = escape(text);

    // If in html tab field name alias is origin field name
    let fieldNameAlias = this.activeTabKey === constants.TAB_HTML.key ? fieldName : newField.fieldName;   
    if(fieldNameAlias)
      fieldNameAlias = escape(fieldNameAlias);

    // Generate html for field-props.
    const fieldHtml = ReportPartFormUtils.buildHTMLCodeFieldContent(newField, true);

    return `<field class="field-wrapper" field-name="${text}">${fieldHtml}${fieldNameAlias}<span class="icon-cancel"></span></field>`;
  }

  /**
   * getHTMLCodeFieldProperties
   * @param  {Object} newField
   * @param  {Object} dataObject
   * @paran  {Boolean} needUpdateSelectedField
   */
  updateHTMLCodeFieldContent(configField: any, field: any, needUpdateSelectedField: boolean): void {
    const updateField = field || this.selectedFieldElement;
    const selectedField = $izenda(updateField)[0];

    if (isNil(selectedField)) {
      return;
    }

    let htmlValue = selectedField.outerHTML;

    htmlValue = $izenda(htmlValue)
      .removeAttr('contenteditable')
      .removeAttr('data-mce-selected');
    const trimHtmlValue = $izenda(htmlValue)[0].outerHTML;

    const originalElementHtml = trimHtmlValue && cloneDeep(trimHtmlValue);
    const result = ReportPartFormUtils.buildHTMLCodeFieldContent(configField);
    const fieldName = escape(configField.fieldName);

    $izenda(selectedField).html(
      (configField.hasSubReport ? '<span class="icon-menu"></span>' : '') +
        result +
        fieldName +
        '<span class="icon-cancel"></span>'
    );

    this.htmlContent = ReportPartFormUtils.cleanHtmlFromTinyMCE(this.htmlContent);

    // update html content with new html of current element.
    this.htmlContent = replace(
      originalElementHtml,
      $izenda(selectedField)[0].outerHTML,
      this.htmlContent.replace(/(\r\n|\n|\r|(\s)+$|((\s)*\t)$)| +(?= )/gm, '')
    );

    this.syncHtmlContentToTinyMCE(needUpdateSelectedField);
  }

  /**
   * Set Form Id
   * @param {[type]} id [description]
   */
  setFormState(activeTabKey, id) {
    if (isNil(this.formState.id)) {
      this.formState.id = id;
    }

    this.activeTabKey = activeTabKey;
  }

  /**
   * Set editorControl
   * @param {Object} editor
   */
  setEditorControl(editor: any): void {
    this.editorControl = editor;
  }

  /**
   * Get editorControl
   * @return {Object}
   */
  getEditorControl(): void {
    return this.editorControl;
  }

  /**
   * Check all fields have function or empty
   * @return {Boolean} [description]
   */
  isAllFieldsHaveFunctionOrEmpty(): boolean {
    return (
      every(element => element.haveFunction === true || element.field.isRunningField, this.allVisibleElements) ||
      every(
        element => !element.haveFunction || element.functionInfo.name === FUNCTION.DAYS_OLD.NAME,
        this.allVisibleElements
      )
    );
  }

  /**
   * Generate previewData description
   * @param {Array} fieldsMapping [description]
   * @param {Array} records       [description]
   * @param {Array} sub total mapping
   * @param {Array} grand total mapping
   * @param {Array} records
   * @param {Object} embedde report data
   * @returns {Object} { htmlContent: require, embeddedReports: optional, embeddedSubReports: optional }
   */
  generatePreviewData(
    props: any,
    fieldsMapping: Array<any>,
    subTotalMapping: Array<any>,
    grandTotalMapping: Array<any>,
    records: Array<any>,
    embeddeReportData: any,
    reportId: string
  ) {
    // for report part viewer, dashboard. when repeaterNodes never created before
    if (isNil(this.repeaterNodes)) {
      let repeaterNodes = [];
      const reportPartElements = get('columns.elements', this).concat(get('separators.elements', this).slice());
      this.reportPartFormRepeater.buildRepeaterTree(this.htmlContent, reportPartElements, 0, repeaterNodes);
      this.repeaterNodes = repeaterNodes;
    }

    if (!this.isAllFieldsHaveFunctionOrEmpty()) {
      return Promise.resolve({ htmlContent: constants.MESSAGES.M12 });
    }

    let result = {};
    const separatorsElement = get('separators.elements', this).slice();
    const columnsElements = get('columns.elements', this).slice();
    const elements = flow(
      concat(separatorsElement),
      filter('isVisible')
    )(columnsElements);

    const filterForSubReport = props.getFilterInfoForSubReport();

    // Date Time Smart Tag
    let newHtmlContent = this.reportPartFormSmartTag.generateDateTimeSmartTag(this.htmlContent);
    // Grandtotal Smart Tag
    newHtmlContent = this.reportPartFormSmartTag.generateGrandTotalSmartTag(
      elements,
      grandTotalMapping,
      records,
      newHtmlContent
    );

    let repeaterNodes = [];
    if (newHtmlContent !== this.htmlContent) {
      this.reportPartFormRepeater.buildRepeaterTree(newHtmlContent, elements, 0, repeaterNodes);
    } else {
      repeaterNodes = this.repeaterNodes;
    }

    // Subtotal Smart Tag
    newHtmlContent = this.reportPartFormSmartTag.validationSubTotalSmartTag(newHtmlContent, repeaterNodes);

    // Check to generate for repeater tag.
    if (
      (newHtmlContent.indexOf(TinyMCEConstants.REPEATER_START) >= 0 ||
        newHtmlContent.indexOf(TinyMCEConstants.REPEATER_START_FORTABLE) >= 0) &&
      fieldsMapping.length > 0
    ) {
      const embeddedSubReports = {
        data: []
      };

      // const currentFields = this.columns.elements.concat(this.separators.elements).slice();
      // TODO: check this logic again.
      const repeaterNode = head(this.repeaterNodes);
      const isValidRunningRepeater = repeaterNode ? get('totalFieldRunningField.length', repeaterNode) === 0 : true;

      let htmlContent = '';

      if (!isValidRunningRepeater) {
        htmlContent = reportDesignerConstants.BUNDLE.MESSAGE.CANNOT_ADD_REPEATER_RUNNING_FIELD;
      } else {
        htmlContent = this.reportPartFormRepeater.generateData(
          newHtmlContent,
          { fieldsMapping, records },
          elements,
          props.reportPart,
          reportId,
          embeddedSubReports,
          filterForSubReport,
          subTotalMapping
        );
      }
      result = {
        htmlContent,
        embeddedReports: [],
        embeddedSubReports: embeddedSubReports.data
      };
    } else {
      // Generate field
      // TODO: Remove when BE validate valid field
      if (isNull(this.dataTree) || props.printingMode || props.viewMode || this.dataTree.hasLoadDataSource) {
        result = this.reportPartFormField.generateField(
          props,
          fieldsMapping,
          records,
          this.properties,
          this.dataTree,
          elements,
          newHtmlContent,
          reportId,
          filterForSubReport
        );
      } else {
        result = {
          htmlContent: '',
          embeddedReports: [],
          embeddedSubReports: []
        };
      }
    }

    // Embedded report
    return this.reportPartFormEmbeddedReport
      .generateEmbeddedReport(embeddeReportData, result.htmlContent)
      .then(embeddedReportObj => {
        result.embeddedReports = embeddedReportObj.embeddedReports;
        result.htmlContent = embeddedReportObj.htmlContent;
        return result;
      });
  }

  /**
   * Check need to break page when printing.
   */
  checkBreakPage(): boolean {
    return this.properties.pageBreakAfterEachEntry || this.checkBreakPageFromHtmlContent();
  }

  /**
   * Verify break page from html content.
   */
  checkBreakPageFromHtmlContent(): boolean {
    const $htmlContentWrapper = $izenda('<div/>').html(this.htmlContent);
    return $izenda('[style^="page-break"]', $htmlContentWrapper).length !== 0;
  }

  /**
   * Convert repeater tag.
   * @param {Number} start position
   * @param {String} active tab key
   */
  convertRepeaterTag(start: number, activeTabKey: string): void {
    if (this.hasRepeaterAndTable(this.htmlContent, activeTabKey)) {
      const placeHolderRoot = {
        placeHolders: []
      };
      this.convertTableToPlaceHolder(placeHolderRoot, activeTabKey);
      this.convertPlaceHolderToTable(placeHolderRoot.placeHolders, activeTabKey);
    }
  }

  /**
   * check to exist repeater and table in content
   * @param {String} content
   * @param {String} active tab key
   * @returns {Boolean} has repeater and table
   */
  hasRepeaterAndTable(content: any, activeTabKey: string): string {
    const repeaterPattern =
      activeTabKey === constants.TAB_HTML.key
        ? TinyMCEConstants.REPEATER_START_FORTABLE
        : TinyMCEConstants.REPEATER_START;
    return content.indexOf('</table>') >= 0 && content.indexOf(repeaterPattern) >= 0;
  }

  /**
   * Convert all table to place holders
   * @param {Object} place holder root
   * @param {String} active tab key
   */
  convertTableToPlaceHolder(placeHolderRoot: string, activeTabKey: string): void {
    const latestBeginTable = this.htmlContent.lastIndexOf('<table');
    const latestEndTable = this.htmlContent.indexOf('</table>', latestBeginTable) + 8;

    const tableContent = this.htmlContent.substring(latestBeginTable, latestEndTable);
    const placeHolderText = '[#TABLE_PLACEHOLDER#' + placeHolderRoot.placeHolders.length + ']';
    this.htmlContent = this.htmlContent.replace(tableContent, placeHolderText);
    placeHolderRoot.placeHolders.push({
      text: placeHolderText,
      content: tableContent
    });

    if (this.htmlContent.indexOf('</table>') >= 0) {
      this.convertTableToPlaceHolder(placeHolderRoot, activeTabKey);
    }
  }

  /**
   * Convert all place holders to table
   * @param {Object} place holders
   * @param {String} active tab key
   */
  convertPlaceHolderToTable(placeHolders: string, activeTabKey: string): void {
    for (let i = placeHolders.length - 1; i >= 0; i--) {
      const placeHolder = placeHolders[i];
      const placeHolderText = placeHolder.text;
      const placeHolderContent = placeHolder.content;

      const content = formatRepeater(placeHolderContent, placeHolderText, activeTabKey);
      this.htmlContent = this.htmlContent.replace(placeHolderText, content);
    }

    function formatRepeater(content, text, activeTabKey) {
      if (activeTabKey === constants.TAB_HTML.key) {
        content = content.split(TinyMCEConstants.REPEATER_START_FORTABLE).join(TinyMCEConstants.REPEATER_START);
        content = content.split(TinyMCEConstants.REPEATER_END_FORTABLE).join(TinyMCEConstants.REPEATER_END);
      } else {
        content = content.split(TinyMCEConstants.REPEATER_START).join(TinyMCEConstants.REPEATER_START_FORTABLE);
        content = content.split(TinyMCEConstants.REPEATER_END).join(TinyMCEConstants.REPEATER_END_FORTABLE);
      }

      return content;
    }
  }

  /**
   * Update html content by changed field name.
   * @param  {String} originalName
   * @param  {String} name
   */
  updateContentWithFieldName(originalName: string, name: string): void {
    if (this.htmlContent && this.htmlContent.indexOf('[' + originalName + ']') !== -1) {
      this.htmlContent = replace('[' + originalName + ']', '[' + name + ']', this.htmlContent);
      this.syncHtmlContentToTinyMCE();
      return true;
    }
    return false;
  }

  /**
   * Set selected field element by field name alias.
   * @param  {String} fieldNameAlias
   */
  setSelectedFieldElement(fieldNameAlias: string): void {
    this.selectedFieldElement = this.getSelecteField(fieldNameAlias);
    this.markDirty();
  }

  /**
   * Get HTML field by field name alias
   * @param {String} fieldNameAlias
   */
  getSelecteField(fieldNameAlias: string, needUpdateSelectedField: boolean): any {
    const $form = this.getTinyMCEContentDOM();
    const $fields = $izenda('field:not(.mce-offscreen-selection field)', $form);
    let matchingField = {};

    forEach(field => {
      !needUpdateSelectedField && $izenda(field).removeAttr('data-mce-selected');
    }, $fields);

    forEach(field => {
      let currentAlias = trim($izenda(field).text());

      if (currentAlias === reportBodyConstants.FIELD_HIDDEN_LABEL) {
        const fieldNameAttr = $izenda(field).attr('field-name');
        const matches = fieldNameAttr.match(/\[(.*?)\]/g);
        currentAlias = matches[matches.length - 1].replace('[', '').replace(']', '');
      }

      if (currentAlias === fieldNameAlias) {
        matchingField = field;
        return false;
      }
    }, $fields);

    if (!needUpdateSelectedField) {
      $izenda(matchingField).attr('contenteditable', false);
      $izenda(matchingField).attr('data-mce-selected', 1);
    }

    return matchingField;
  }

  /**
   * Sync htmlContent to TinyMCE content.
   * @param {Boolean} needUpdateSelectedField
   */
  syncHtmlContentToTinyMCE(needUpdateSelectedField: boolean): void {
    const form = tinymce.get(this.editorControl.id);
    form.setContent(this.htmlContent);

    !needUpdateSelectedField && this.setSelectedFieldElement(trim($izenda(this.selectedFieldElement).text()));
  }

  /**
   * Get TinyMCE content DOM.
   */
  getTinyMCEContentDOM(): string {
    const tinyMCEIFramePost = '_ifr';
    return $izenda('#' + this.formState.id + tinyMCEIFramePost).contents();
  }

  /**
   * Get drill info.
   */
  getDrilldownInfo(): any {
    return {
      expandedLevel: -1,
      filters: []
    };
  }

  /**
   * Check dirty status
   * @return {[boolean]} is dirty
   */
  checkDirty(): boolean {
    return this.isDirty;
  }

  /**
   * Clean dirty status
   */
  cleanDirty(): void {
    this.resetDirty();
  }

  /**
   * Mark isFormatted flag
   */
  markFormated(): void {
    this.isFormatted = true;
  }

  /**
   * check if function is compatible with element
   * @method checkedCompatibleFunction
   * @param  {Object}                  container    Container that element belong
   * @param  {Object}                  element      Element need to validate
   * @param  {Object}                  functionInfo Function infomation
   * @return {Boolean}                              Result
   */
  checkedCompatibleFunction(container: any, element: Array<any>, functionInfo: any): boolean {
    if (functionInfo.name && element.field.hasAggregatedFunction) {
      return false;
    }

    return true;
  }

  /**
   * Update subtotal's levels property
   */
  @action
  updateSubtotalLevels(): void {
    const elements = get('columns.elements', this).concat(get('separators.elements', this).slice());

    if (isNil(this.repeaterNodes)) {
      let repeaterNodes = [];
      this.reportPartFormRepeater.buildRepeaterTree(this.htmlContent, elements, 0, repeaterNodes);
      this.repeaterNodes = repeaterNodes;
    }

    const lastFieldNameAliasChain = [];

    this.clearSubtotalLevels(elements);

    const resolveLastFieldNameAlias = repeater => {
      if (isEmpty(repeater.fields)) {
        return isEmpty(lastFieldNameAliasChain) ? '' : last(lastFieldNameAliasChain);
      }
      const lastFieldNameAlias = getOr('', 'fieldInfo.fieldNameAlias', last(repeater.fields));
      lastFieldNameAliasChain.push(lastFieldNameAlias);
      return lastFieldNameAlias;
    };

    const process = nodes => {
      forEach(repeater => {
        const lastFieldNameAlias = resolveLastFieldNameAlias(repeater);
        flow(
          filter(tag => !!get('configField.fieldInfo', tag)),
          forEach(subtotalTag => {
            const properties = subtotalTag.configField.fieldInfo.properties;
            const subTotal = invokeArgs('getDataValueByKey', ['subTotal'], properties);
            if (!!subTotal.label) {
              if (!isArray(subTotal.levels)) {
                subTotal.levels = [lastFieldNameAlias];
              } else {
                findIndex(i => i === lastFieldNameAlias, subTotal.levels) === -1 &&
                  subTotal.levels.push(lastFieldNameAlias);
              }
              invokeArgs('setDataFormatByKey', ['subTotal', subTotal], properties);
            }
          })
        )(repeater.smartTagInContent);

        !isEmpty(repeater.childNodes) && process(repeater.childNodes);
        lastFieldNameAliasChain.pop();
      }, nodes);
    };

    process(this.repeaterNodes);
  }

  /**
   * Clear subtotal's levels
   * @param {Array<Object} elements
   */
  @action
  clearSubtotalLevels(elements: Array<any>): void {
    forEach(({ properties }) => {
      const subTotal = invokeArgs('getDataValueByKey', ['subTotal'], properties);
      if (!!subTotal.label && !isEmpty(subTotal.levels)) {
        subTotal.levels = [];
        invokeArgs('setDataFormatByKey', ['subTotal', subTotal], properties);
      }
    }, elements);
  }

  /**
   * read data from api/create report part to update data
   * @param {Object} content [data from api/create report part]
   */
  @action
  readData(content: any) {
    content = content || {};

    this.isCrossFiltering = content.isCrossFiltering;
    this.isFormatted = content.isFormatted;
    this.columns = populateReportPartContainer('columns', content.columns);
    this.separators = populateReportPartContainer('separators', content.separators);
    this.setHTMLContent(content.htmlContent || '');

    this.rebuildHtml();
    this.setReportPartProperties(content.properties);

  }

  // Sync HTML to element for form when change from backside to front side
  @action
  afterChangeSide(isBackSide: boolean) {
    !isBackSide && this.syncFromHTMLToElements();
  }

  /**
   * Build repeater tree
   */
  @action
  buildRepeaterTree(setSelectedFieldElement: boolean) {
    const elements = get('columns.elements', this).concat(get('separators.elements', this).slice());
    let repeaterNodes = [];
    this.reportPartFormRepeater.buildRepeaterTree(this.htmlContent, elements, 0, repeaterNodes);

    this.repeaterNodes = repeaterNodes;
    // reset hasRepeaterPart
    forEach(field => {
      field && (field.properties.hasRepeatPart = false);
    }, elements);

    this.repeaterNodes = repeaterNodes;

    // update sort value & hasRepeaterPart also
    const updateFieldsProperty = repeaterNodes => {
      if (isEmpty(repeaterNodes)) {
        return;
      }
      forEach(repeaterNode => {
        const hasSupportUnsort = this.checkRepeaterHasSupportUnsort(repeaterNode);
        // update list field config in repeater part
        forEach(field => {
          const fieldModel = field.fieldInfo || {};
          const onlySupportUnsort = this.checkFieldOnlySupportUnsort(this.repeaterNodes, repeaterNode, fieldModel);
          this.updateSortValue(fieldModel, setSelectedFieldElement, onlySupportUnsort, hasSupportUnsort);
          fieldModel.properties.hasRepeatPart = true;
        }, repeaterNode.fields);

        updateFieldsProperty(repeaterNode.childNodes);
      }, repeaterNodes);
    };
    updateFieldsProperty(repeaterNodes);
  }

  /**
   * Update sort value for field
   * @param {Object} fieldModel
   * @param {boolean} setSelectedFieldElement
   * @param {boolean} onlySupportUnsort
   * @param {boolean} hasSupportUnsort
   */
  updateSortValue(
    fieldModel: any,
    setSelectedFieldElement: boolean,
    onlySupportUnsort: boolean,
    hasSupportUnsort: boolean
  ): void {
    const currentSortValue = invokeArgs('getValueByKey', ['sort'], fieldModel.properties);
    const newValue = onlySupportUnsort
      ? UNSORTED
      : hasSupportUnsort || currentSortValue === DESC
      ? currentSortValue
      : ASC;

    if (newValue !== currentSortValue) {
      invokeArgs('setDataFormatByKey', ['sort', newValue], fieldModel.properties);
      const selecteField = this.getSelecteField(fieldModel.field.fieldNameAlias, true);
      setSelectedFieldElement && selecteField && this.updateHTMLCodeFieldContent(fieldModel, selecteField, true);
    }
  }

  /**
   * Rebuild HTML Content
   * @param {Boolean} setSelectedFieldElement
   */
  @action
  rebuildHTMLContent(needUpdateSelectedField: boolean): any {
    // Update html content from tinyMCE to report part properties for case add repeater from propeties panel
    const formEditor = tinymce.get(get('editorControl.id', this));

    formEditor && (this.htmlContent = formEditor.getContent());

    this.buildRepeaterTree(needUpdateSelectedField);
    return Promise.resolve(true);
  }

  @action
  updateElementPropsForRepeater = debounce(1000, () => {
    this.buildRepeaterTree(false);
  });

  /**
   * Get Embedded Report HTML
   * @param  {Array} List data of report part object
   * @return {String} embedded report html
   */
  @action
  insertEmbeddedReportHtml(data: any): void {
    this.reportPartFormEmbeddedReport.insertEmbeddedReportHtml(data, this.editorControl);
  }

  /**
   * Update Embedded Report HTML
   * @param  {Array} List data of report part object
   * @return {String} embedded report html
   */
  @action
  updateEmbeddedReportHtml(embeddedReportDOM: any, model: any): void {
    this.reportPartFormEmbeddedReport.insertEmbeddedReportHtml(model, this.editorControl);
  }
  /**
   * Update subtotal HTML
   * @param  {String} List data of report part object
   */
  @action
  updateSubTotalHtml(fieldName: string, selectedElement: any): void {
    this.reportPartFormSmartTag.updateSubTotalHtml(fieldName, selectedElement);
  }
  /**
   * Update grand total Report HTML
   * @param  {String} fieldname
   */
  @action
  updateGrandTotalHtml(fieldName: string, selectedElement: any): void {
    this.reportPartFormSmartTag.updateGrandTotalHtml(fieldName, selectedElement);
  }

  /**
   * Update container field
   * @return {[type]} [description]
   */
  @action
  updateContainerField(): void {
    const result = this.reportPartFormField.updateContainerField(this.columns.elements, this.htmlContent);
    if (!isNil(result)) {
      this.columns.elements = result;
    }
  }

  changePageSize(pageSize: number): void {
    this.properties.pageSize = pageSize;
  }

  /**
   * Action when report part is in config mode (report part properties are showed)
   */
  configModeAction(): void {
    this.isActiveForEdit = false;
  }

  /**
   * Reset border default value
   */
  @action
  resetBorderDefaultValue(): void {
    this.properties.isVisibleBorder = false;
    this.properties.borderColor = constants.FORM_STYLE_DEFAULT.borderColor;
    this.properties.borderStyle = constants.FORM_STYLE_DEFAULT.borderStyle;
    this.properties.borderWidth = constants.FORM_STYLE_DEFAULT.borderWidth;

    this.markDirty();
  }

  /**
   * Reset refesh intervalue default value
   * @param  {[type]} ) {               this.properties.dataRefreshInterval [description]
   * @return {[type]}   [description]
   */
  @action
  resetRefreshIntervalValue(): void {
    this.properties.dataRefreshInterval = constants.DEFAULT_SETTING_VALUE.refreshIntervalDefault;
    this.markDirty();
  }
  /**
   * Move all field from seperator to column container for old reporpt.
   */
  @action
  rearrangeFieldInColumnContainer(): void {
    const totalFields = get('totalFields', head(this.repeaterNodes));

    this.columns.elements.splice(0, this.columns.elements.length);

    forEach(item => {
      item && item.fieldInfo && this.columns.elements.push(item.fieldInfo);
    }, totalFields);

    this.separators.elements.splice(0, this.separators.elements.length);
  }

  /**
   * Found repeater node by field
   * @param {String} filedNameAlias
   */
  foundRepeaternode(filedNameAlias: string, repeaterNodes: RepeaterNode): any {
    let repeaterNode;

    const foundNode = (filedNameAlias, object) => {
      if (isEmpty(object)) return null;
      const matchLvl = find(['fieldInfo.fieldNameAlias', filedNameAlias], object.fields);
      if (matchLvl) {
        repeaterNode = object;
      } else {
        forEach(item => {
          foundNode(filedNameAlias, item);
        }, object.childNodes);
      }
    };

    forEach(item => {
      foundNode(filedNameAlias, item);
    }, repeaterNodes);

    return repeaterNode;
  }

  /**
   * Hidden sort properties on property panel
   * @param  {Object} field
   * @return {Boolean}
   */
  checkHiddenSortField(field: any): boolean {
    // TODO: this logic will consider remove later when backend implement support for data grouping.
    const repeaterNode = this.foundRepeaternode(field.fieldNameAlias, this.repeaterNodes);

    return this.checkFieldOnlySupportUnsort(this.repeaterNodes, repeaterNode, field);
  }

  /**
   * Check field only support unsort function.
   * @param {*} repeaterNodes
   * @param {*} repeaterNode
   * @param {*} field
   */
  checkFieldOnlySupportUnsort(repeaterNodes: Array<RepeaterNode>, repeaterNode: RepeaterNode, field: any): boolean {
    if (isEmpty(repeaterNode) || repeaterNode.childNodes.length > 0 || repeaterNode.level === 0) {
      return false;
    }
    // For repeater, if data has grouping, fields has aggregated function must be unsorted
    // Need check more conditions cause isAggregatedField propeties not correct.
    if (get('hasGroupingData', head(repeaterNodes))) {
      return (
        !field.functionInfo.userDefined &&
        field.isAggregatedField &&
        get('functionInfo.name', field) !== FUNCTION.DAYS_OLD.NAME
      );
    }

    return false;
  }

  /**
   * Check repeater has support unsort value
   * @param {Object} repeaterNode
   */
  checkRepeaterHasSupportUnsort(repeaterNode: RepeaterNode): boolean {
    // If no repeater.
    if (isNil(repeaterNode) || (repeaterNode.level === 0 && repeaterNode.hasSupportUnsort)) {
      return true;
    }
    const supportUnsortLastLevel =
      repeaterNode.hasSupportUnsort && get('validSubtotalSmartTag.length', repeaterNode) === 0;
    return get('hasGroupingData', head(this.repeaterNodes))
      ? get('allFieldIsNotGroupingData', head(this.repeaterNodes))
        ? supportUnsortLastLevel
        : false
      : supportUnsortLastLevel;
  }

  /**
   * Check field can have unsort value on propeties panel.
   * @param {Object} container
   * @param {Object} reportPartElement
   */
  checkCanUnsort(container: any, reportPartElement: any): boolean {
    const repeaterNode = this.foundRepeaternode(reportPartElement.fieldNameAlias, this.repeaterNodes);

    return this.checkRepeaterHasSupportUnsort(repeaterNode);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartForm.js